/* eslint-disable prettier/prettier */
import React, { useState, useRef, useEffect } from "react"

import { compiler } from "markdown-to-jsx"
import styled from "styled-components"

import {
  colors,
  oldBreakpoints as breakpoints,
  spacing,
  typography,
  elevation,
} from "@izettle/otto"
import { Button as ZButton, ButtonProps, Text, TextAnchor } from "@izettle/react"

type SvgContainerProps = {
  margin?: string
  padding?: string
  color?: string
  backgroundColor?: string
}

type SvgProps = {
  html: string
} & SvgContainerProps

export const PageView = styled.div`
  margin: ${spacing.large} auto;
  width: 560px;

  @media (max-width: ${breakpoints.medium}) {
    width: 100%;
  }
`

export const Page = styled.div`
  font-family: Zen;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const View = styled.div<{ withShadow?: boolean }>`
  position: relative;
  background-color: ${colors.background.default};
  border-radius: ${spacing.xSmall};
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 718px;
  width: 560px;
  ${({ withShadow }) => (withShadow ? `box-shadow: ${elevation.level4};` : "")}

  @media (max-width: ${breakpoints.medium}) {
    width: 100%;
  }

  @media (max-height: ${breakpoints.large}) {
    height: calc(100% - 64px);
  }

  @media (max-width: ${breakpoints.medium}) and (max-height: ${breakpoints.xLarge}) {
    height: 100%;
    width: 100%;
    border-radius: 0px;
    border: none;
    box-shadow: none;
  }
`

export const Container = styled(View)<{ withNoHeaderSpace?: boolean; }>`
  background-color: transparent;
  border: 0;
  padding-top: 80px;
  height: calc(100% - 80px);
  overflow: unset;

  ${({ withNoHeaderSpace }) => (withNoHeaderSpace ? `
    padding-top: 0px;
    height: 100%;
  ` : "")}

  overflow: auto;

  background:
    /* Shadow Cover TOP */
    linear-gradient(
      white 30%,
      rgba(255, 255, 255, 0)
    ) center top,
    
    /* Shadow Cover BOTTOM */
    linear-gradient(
      rgba(255, 255, 255, 0), 
      white 70%
    ) center bottom,
    
    /* Shadow TOP */
    radial-gradient(
      farthest-side at 50% 0,
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0)
    ) center top,
    
    /* Shadow BOTTOM */
    radial-gradient(
      farthest-side at 50% 100%,
      rgba(50, 35, 107, 0.6),
      rgba(0, 0, 0, 0)
    ) center bottom;
  
  background-repeat: no-repeat;
  background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
  background-attachment: local, local, scroll, scroll;
`

export const Logo = styled.img`
  width: ${spacing.xLarge};
  height: ${spacing.xLarge};
`
export const LogoSmall = styled.img`
  width: 40px;
  height: 40px;
`

export const Header = styled.div`
  background-color: ${colors.background.default};
  position: absolute;
  top: 0;
  width: 100%;
  display: flex;
  min-height: 80px; // 24 * 3
  transition: opacity 200ms;
  z-index: 10;
  /* border-bottom: 1px solid ${colors.border.divider}; */

  &.hidden {
    opacity: 0;
    visibility: hidden;
  }
`
export const Footer = styled.div`
  /* min-height: ${spacing.huge}; */
  /* border-top: 1px solid ${colors.border.divider}; */
  padding: ${spacing.small};
  display: flex;

  @media (min-width: ${breakpoints.large}) and (min-height: ${breakpoints.large}) {
    padding: ${spacing.large};
  }
`
export const Content = styled.div<{ vCentered?: boolean; hCentered?: boolean; hasScroll?: boolean; withNoHeaderSpace?: boolean; withMoreFooterSpace?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${spacing.small};

  @media (min-width: ${breakpoints.large}) and (min-height: ${breakpoints.large}) {
    padding: ${spacing.large};
  }
  ${({ hasScroll }) => (hasScroll ? "overflow: scroll;" : "overflow: unset;")}
  ${({ vCentered }) => (vCentered ? "align-items: center;" : "")}
  ${({ hCentered }) => (hCentered ? "justify-content: center; text-align: center;" : "")}
  ${({ withNoHeaderSpace }) => (withNoHeaderSpace ? "margin-top: -80px;" : "")}
  ${({ withMoreFooterSpace }) => (withMoreFooterSpace ? "margin-bottom: 80px;" : "")}
`

export const Row = styled.div`
  width: 100%;
  margin: auto;
`

export const Flex = styled.div<{ vCentered?: boolean, hCentered?: boolean, direction?: string }>`
  display: flex;
  ${({ vCentered }) => (vCentered ? "align-items: center;" : "")}
  ${({ hCentered }) => (hCentered ? "justify-content: center;" : "")}
  ${({ direction }) => (direction ? `flex-direction:${direction}` : "")}
`

export enum Alignments {
  Left = "left",
  Centered = "Centered",
}

export const HeaderContent = styled.div<{ alignment?: Alignments }>`
  margin: ${spacing.small};
  flex: 1;
  display: flex;
  align-items: center;
  ${({ alignment }) => alignment === Alignments.Centered && "justify-content: center"};
`

export const Button = styled(ZButton)`
  flex: 1;
`

export const Title1 = styled(Text.Title1)`
  margin: '${spacing.small} 0 0 0';
`

export const { Title2 } = Text

export const RegularText = Text.Regular

export const RegularSecondaryText = Text.RegularSecondary

export const LargeText = styled(Text.Large)`
  font-size: ${spacing.large};
  line-height: 40px;
`

export const SmallText = Text.Small

export const SmallSecondaryText = Text.SmallSecondary

export const Anchor = styled(TextAnchor)`
  font-weight: bold;
`

export const AnchorButton = styled(ZButton).attrs({ variant: "inline" })<ButtonProps>`
  font-size: 20px!important;
  margin: 0;
  padding: 0!important;
  min-height: auto!important;
  text-decoration-line: none!important;
  span {
    justify-content: start;
  }

  ${({ size }) => (size === "small" ? `
    font-size: 14px!important;
  `: "")};
`

export const IconAnchorButton = styled.button`
  margin: 0;
  border: 0;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
  text-align: start;
  svg {
    border-radius: ${spacing.large};
    padding: ${spacing.tiny};
  }

  :focus {
    outline: none;
    svg {
      box-shadow: 0 0 0px 1px ${colors.action.secondary.border.hover};
      background-color: ${colors.action.secondary.background.hover};
    }
  }
`

export const Wall = styled.div`
  padding: 0 ${spacing.small};
  border-radius: ${spacing.xSmall};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
`

export const WallSuccess = styled(Wall)`
  background-color: ${colors.background.success};
  color: ${colors.text.success};
  h2, p, button, a {
    color: ${colors.text.success}!important;
  }
`

export const WallHighlight = styled(Wall)`
  background-color: ${colors.background.highlight};
  color: ${colors.text.highlight};
  h2, p, button, a {
    color: ${colors.text.highlight};
  }
`

export const ErrorSpan = styled.span.attrs({ role: "alert" })`
  ${typography.small};
  display: block;
  margin-top: ${spacing.tiny};
  word-break: break-word;
  color: ${colors.text.error};
`

export const Item = styled.div`
  display: flex;
  margin-bottom: ${spacing.medium};
  align-items: center;
  button {
    font-weight: 500;
    font-size: 18px;
    font-family: Zen;
    line-height: 28px;
  }
`

export const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${colors.border.divider};
  width: 100%;
`

export const Form = styled.form`
  flex: 1;
  height: 100%;
`

export const SvgContainer = styled.span<SvgContainerProps>`
  border-radius: 120px;
  display: inline-flex;
  ${({ margin, color, padding, backgroundColor }) => `
    margin: ${margin};
    padding: ${padding};
    background-color: ${backgroundColor};
    svg {
      color: ${color};
    }
  `}
`

export const ProgressDiv = styled.div`
  height: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50%;
  background-color: ${colors.action.primary.background.default};
  z-index: 2;
  transition: width 200ms, opacity 200ms;
`

type ProgressProps = {
  index: number
  startIndex: number
  endIndex: number
}

export const Progress = ({ index, startIndex, endIndex }: ProgressProps) => {
  const isInRange = startIndex <= index && index <= endIndex

  if (!isInRange) {
    return <ProgressDiv style={{ width: 0 }} />
  }

  const fullRange = endIndex - startIndex + 1
  const currentIndex = index - startIndex + 1

  return <ProgressDiv style={{ width: `${(currentIndex / fullRange) * 100}%` }} />
}

export const SvgIcon = ({
  html,
  margin,
  padding,
  color,
  backgroundColor = "transparent",
}: SvgProps) => (
  <SvgContainer
    margin={margin ?? "0"}
    padding={padding ?? "0"}
    color={color ?? colors.icon.default}
    backgroundColor={backgroundColor}
    aria-hidden
  >
    {compiler(html)}
  </SvgContainer>
)

export const IconButton = React.forwardRef(({ html, margin, color, ...props }: any, ref) => (
  <IconAnchorButton ref={ref} {...props}>
    <SvgIcon html={html} margin={margin} color={color} />
  </IconAnchorButton>
))


export const LargeInputContainer = styled.div`
  position: relative;
  margin-top: ${spacing.huge};
  margin-bottom: ${spacing.tiny};
  display: flex;
  align-items: center;

  label {
    position: absolute;
    top: 0;
    font-size: ${spacing.large};
    transition: all 0.1s cubic-bezier(0.25, 0.1, 0.25, 1) 0s, color 0.1s 0s;
    color: ${colors.text.placeholder};
    cursor: text;
    z-index: -1;
    font-weight: bold;

    &.up {
      left: 0!important;
      top: -${spacing.large};
      font-size: ${spacing.small};
      color: ${colors.text.highlight};
      cursor: default;
    }
  }
`

export const Input = styled.input`
  border: 0;
  padding: 0;
  background-color: transparent;
  color: ${colors.text.default};
  border-radius: 0px;
  outline: none;
  font-family: Zen;
  font-weight: 600;
  font-size: ${spacing.large};
  line-height: 40px;
  width: 100%;
  ::placeholder {
    color: ${colors.text.placeholder};
  }
  @media (max-height: 400px) {
    margin-top: ${spacing.tiny};
  }
`

export type LargeInputProps = {
  control: any
  placeholder: string,
  type: string,
  name: string,
  prefixElement?: React.ReactNode
  onChange?: (_event: any) => void
  value?: any
  labelLeftPosition?: string
  ariaInvalid?: boolean
}

export const LargeInput = ({
  prefixElement,
  name,
  type,
  placeholder,
  control,
  labelLeftPosition,
  onChange,
  value,
  ariaInvalid = true,
}: LargeInputProps) => {
  const [inputIsFocused, setInputIsFocused] = useState(false)
  const { register } = control
  const containerRef = useRef<HTMLDivElement>(null)

  const handleOnFocus = () => {
    setInputIsFocused(true)
    document.body.classList.add('focus')
  }
  const handleOnBlur = () => {
    const input = containerRef?.current?.querySelector("input")
    setInputIsFocused(!!input?.value)
    document.body.classList.remove('focus')
  }

  const registerProps = register(name)

  useEffect(() => {
    if (value) {
      setInputIsFocused(true)
    }
  }, [value])

  return (
    <LargeInputContainer ref={containerRef}>
      {prefixElement}
      <label
        style={{ left: labelLeftPosition }}
        htmlFor={name}
        className={inputIsFocused ? "up" : undefined}
      >
        {placeholder}
      </label>
      <Input
        id={name}
        type={type}
        {...registerProps}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={onChange ?? registerProps.onChange}
        value={value ?? registerProps.value}
        aria-invalid={ariaInvalid}
        className="focus"
      />
    </LargeInputContainer>
  )
}


export const SlideWrapper = styled.div`
  height: 100%;
  flex: 1;
  &.hidden {
    transition-delay: 0.5s;
    visibility: hidden;
  }
`